import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useProject } from '@/contexts/ProjectContext';

export interface SipSettings {
  wsUrl: string | null;
  domain: string | null;
  username: string | null;
  password: string | null;
  callerId: string | null;
  enabled: boolean;
}

export interface UpdateSipSettingsDto {
  wsUrl?: string;
  domain?: string;
  username?: string;
  password?: string;
  callerId?: string;
  enabled?: boolean;
}

export function useSipSettings() {
  const { currentProject } = useProject();
  const projectId = currentProject?.id;

  return useQuery({
    queryKey: ['sip-settings', projectId],
    queryFn: () =>
      api.get<SipSettings>(`/sip/settings?projectId=${projectId}`),
    enabled: !!projectId,
  });
}

export function useUpdateSipSettings() {
  const queryClient = useQueryClient();
  const { currentProject } = useProject();
  const projectId = currentProject?.id;

  return useMutation({
    mutationFn: (data: UpdateSipSettingsDto) =>
      api.post<{ success: boolean; settings: SipSettings }>(
        `/sip/settings?projectId=${projectId}`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sip-settings'] });
    },
  });
}

export function useUploadRecording() {
  const queryClient = useQueryClient();
  const { currentProject } = useProject();
  const projectId = currentProject?.id;

  return useMutation({
    mutationFn: async ({
      blob,
      activityId,
      fileName,
    }: {
      blob: Blob;
      activityId: string;
      fileName?: string;
    }) => {
      const formData = new FormData();
      formData.append(
        'file',
        blob,
        fileName || `call_${Date.now()}.webm`,
      );

      return api.uploadFile<{ success: boolean; url: string }>(
        `/sip/recording?activityId=${activityId}&projectId=${projectId}`,
        formData,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}
